// Claude API's key (Poulpe-585), kept in this Mac's login Keychain by the Mac
// app (mac/Sources/PoulpeMacUI/ClaudeApiKey.swift), where the runner on this
// Mac reads it. The key goes from this page to the shell and nowhere else:
// never to the server, which only hears from the runner whether one is set.
// The shell names the runner installed on this Mac with
// `data-claude-api-key="<runner id>"` on <html> (Poulpe-718): the key is kept
// for that runner alone, so the page offers it for no other. The page posts
// `{claudeApiKey: {id, document, key}}` or `{..., remove: true}` and the shell
// answers with a `poulpe-claude-api-key` event: `{id, set}`, or `{id, error}`.
import { hasShell, tellShell } from '../lib/shell'
import { uuid4 } from './uuid'

const ANSWER = 'poulpe-claude-api-key'
/** `security` answers within a second; past this, the shell is not answering. */
const PATIENCE = 20_000

/** The runner installed on this Mac, whose key the Mac app keeps; null outside it, or when none is installed. */
export function claudeApiKeyRunner(): string | null {
  if (typeof document === 'undefined' || !hasShell()) return null
  return document.documentElement.dataset.claudeApiKey || null
}

/** Whether this page can keep Claude API's key for `runner`: inside the Mac app, for the runner installed on that Mac. */
export function canKeepClaudeApiKey(runner: string): boolean {
  return runner !== '' && claudeApiKeyRunner() === runner
}

/**
 * Keeps `key` in this Mac's Keychain for `runner`, or forgets the one there
 * (null). Resolves once the shell says the Keychain holds that key, or none;
 * any other answer is a failure, never a save.
 */
export function keepClaudeApiKey(runner: string, key: string | null): Promise<void> {
  const document = globalThis.document?.documentElement.dataset.sessionDocument
  if (!canKeepClaudeApiKey(runner) || !document) return Promise.reject(new Error('Only the Poulpe app on this Mac can keep the key.'))
  return new Promise<void>((resolve, reject) => {
    const id = uuid4()
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail
      if (typeof detail !== 'object' || detail === null || (detail as { id?: unknown }).id !== id) return
      window.removeEventListener(ANSWER, listener)
      window.clearTimeout(timeout)
      const { set, error } = detail as { set?: unknown; error?: unknown }
      if (set === (key !== null)) return resolve()
      reject(new Error(error === 'refused' ? 'That is not an API key.' : 'The Keychain could not be changed.'))
    }
    const timeout = window.setTimeout(() => {
      window.removeEventListener(ANSWER, listener)
      reject(new Error('The Poulpe app did not answer.'))
    }, PATIENCE)
    window.addEventListener(ANSWER, listener)
    tellShell({ claudeApiKey: key === null ? { id, document, remove: true } : { id, document, key } })
  })
}
